import categoriesAtom from "@/atoms/categories"
import type { WonPrize } from "@/domain/marketing/api"
import { prizeExpired, prizeForCode } from "@/domain/marketing/won-prize"
import { getMerchantId } from "@/lib/company-storage"
import type { CouponBenefit } from "@/lib/coupon-benefit"
import { readCouponDetails } from "@/lib/coupon-readiness"
import { evaluateCoupons } from "@/lib/discount-evaluation/api"
import { buildEvaluationLines, cartLineFor } from "@/lib/discount-evaluation/cart-lines"
import { describeRejection, type CouponNotice } from "@/lib/discount-evaluation/coupon-copy"
import type { CouponEvaluation, EvaluateCouponsRequest } from "@/lib/discount-evaluation/types"
import { couponCodeKey, getKeptCouponCodes, getPendingCouponCodes } from "@/lib/pending-coupon"
import type { Cart } from "@/types"
import { useAtomValue } from "jotai"
import { useEffect, useRef, useState } from "react"
import { useAuthStatus } from "./use-auth-status"
import { useWonPrizes } from "./use-won-prizes"

const EVALUATION_DEBOUNCE_MS = 300

/**
 * Where an attached prize stands on this bag: `applied` with what
 * coupon-service takes off, `delivery` waits for the delivery fee (known only
 * at checkout), `missing-item` is an item prize with nothing in the bag to
 * discount, `blocked` explains another reason, `unchecked` could not ask.
 */
export type CartPrizeState =
  | { kind: "checking" }
  | { kind: "applied"; discountCents: number }
  | { kind: "delivery" }
  | { kind: "missing-item" }
  | { kind: "blocked"; notice: CouponNotice }
  | { kind: "unchecked" }

/** A won prize attached to this visit's order, with what it gives and how it stands on the bag. */
export type CartPrize = { prize: WonPrize; benefit: CouponBenefit | null; state: CartPrizeState }

/** The prizes on the bag, the server's discount per bag line (by index) and their total, in cents. */
export type CartPrizes = { prizes: CartPrize[]; lineDiscountCents: readonly number[]; discountCents: number }

type Snapshot = { key: string; data: CouponEvaluation | null }

function attachedCodes(merchantId: string | null): string[] {
  if (!merchantId) return []
  return [...new Set([...getPendingCouponCodes(merchantId), ...getKeptCouponCodes(merchantId)])]
}

function stateOf(code: string, benefit: CouponBenefit | null | undefined, evaluation: CouponEvaluation | null, settled: boolean, cart: Cart): CartPrizeState {
  if (benefit === undefined) return { kind: "checking" }
  if (benefit?.kind === "delivery") return { kind: "delivery" }
  if (!settled) return { kind: "checking" }
  if (!evaluation) return { kind: "unchecked" }
  const applied = evaluation.allocations.filter((allocation) => couponCodeKey(allocation.couponCode) === couponCodeKey(code))
  const discountCents = applied.reduce((sum, allocation) => sum + allocation.amountCents, 0)
  if (discountCents > 0) return { kind: "applied", discountCents }
  const rejected = evaluation.rejected.find((entry) => couponCodeKey(entry.code) === couponCodeKey(code))
  if (rejected?.reason === "EVALUATION_REASON_NO_TARGET_IN_CART" && benefit?.kind === "items") return { kind: "missing-item" }
  if (rejected) return { kind: "blocked", notice: describeRejection(rejected, cart, evaluation.maxCouponsPerOrder) }
  return { kind: "unchecked" }
}

/**
 * The won prizes attached to this visit's order (pending or kept codes that
 * are prizes of this customer), each evaluated on the bag by coupon-service
 * the way checkout will (`coupons/evaluate`, debounced, cancelled when the bag
 * moves on). The delivery fee is unknown before checkout, so a free-delivery
 * prize is listed without an amount. Nothing is priced on the client.
 */
export function useCartPrizes(cart: Cart): CartPrizes {
  const authenticated = useAuthStatus()
  const categories = useAtomValue(categoriesAtom)
  const merchantId = getMerchantId()
  const codes = attachedCodes(merchantId)
  const won = useWonPrizes(authenticated && codes.length > 0)
  const now = new Date()
  const prizes = won.status === "ready"
    ? codes.flatMap((code) => {
        const prize = prizeForCode(won.prizes, code)
        return prize && !prizeExpired(prize, now) ? [prize] : []
      })
    : []
  const [benefits, setBenefits] = useState<Record<string, CouponBenefit | null>>({})
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)

  const missing = prizes.map((prize) => prize.code).filter((code) => !(code in benefits))
  const missingKey = missing.join(",")
  useEffect(() => {
    if (!merchantId || !missingKey) return
    let cancelled = false
    for (const code of missingKey.split(",")) {
      void readCouponDetails(merchantId, code).then((details) => {
        if (!cancelled) setBenefits((current) => ({ ...current, [code]: details?.benefit ?? null }))
      })
    }
    return () => {
      cancelled = true
    }
  }, [merchantId, missingKey])

  const evaluated = prizes.map((prize) => prize.code).filter((code) => code in benefits && benefits[code]?.kind !== "delivery")
  const request: EvaluateCouponsRequest | null =
    authenticated && evaluated.length > 0 && cart.items.length > 0
      ? { couponCodes: evaluated, lines: buildEvaluationLines(cart, categories), deliveryFeeCents: 0, cartBenefitDiscountCents: 0, deliveryBenefitDiscountCents: 0 }
      : null
  const key = request ? JSON.stringify(request) : ""
  const latestRequest = useRef(request)

  useEffect(() => {
    latestRequest.current = request
  })

  useEffect(() => {
    const body = latestRequest.current
    if (!key || !merchantId || !body) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      evaluateCoupons(merchantId, body, controller.signal).then(
        (data) => setSnapshot({ key, data }),
        () => {
          if (!controller.signal.aborted) setSnapshot({ key, data: null })
        },
      )
    }, EVALUATION_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [key, merchantId])

  const settled = request === null || snapshot?.key === key
  const evaluation = settled && snapshot?.key === key ? snapshot.data : null
  const views = prizes.map((prize) => ({
    prize,
    benefit: benefits[prize.code] ?? null,
    state: stateOf(prize.code, benefits[prize.code], evaluation, settled, cart),
  }))
  const lineDiscountCents = cart.items.map(() => 0)
  let discountCents = 0
  for (const allocation of evaluation?.allocations ?? []) {
    if (!evaluated.some((code) => couponCodeKey(code) === couponCodeKey(allocation.couponCode)) || allocation.amountCents <= 0) continue
    discountCents += allocation.amountCents
    const onLine = allocation.target === "ALLOCATION_TARGET_LINE" || allocation.target === "ALLOCATION_TARGET_OPTION"
    if (onLine && cartLineFor(cart, allocation.lineId)) lineDiscountCents[Number(allocation.lineId)] += allocation.amountCents
  }
  return { prizes: views, lineDiscountCents, discountCents }
}
